const EXPIRY_REFRESH_BUFFER_MS = 5000;
const REFRESH_COOLDOWN_MS = 3000;
const REFRESH_STARTED = Symbol.for('kiosk.csrfRefresh.started');
const SUBMIT_GUARD_STARTED = Symbol.for('kiosk.csrfSubmitGuard.started');

let refreshUrl = '';
let expiryRefreshEnabled = false;
let expiryTimerId = 0;
let refreshInFlight = null;
let lastSuccessfulRefreshAt = 0;
let csrfInitialized = false;
const refreshingForms = new WeakSet();

function metaContent(name) {
    const el = document.querySelector('meta[name="' + name + '"]');
    return el ? String(el.getAttribute('content') || '').trim() : '';
}

export function currentCsrfToken() {
    const token = metaContent('csrf-token');
    if (!token) {
        throw new Error('currentCsrfToken: meta[name="csrf-token"] is required');
    }
    return token;
}

function requireRefreshUrl() {
    const url = metaContent('csrf-refresh-url');
    if (!url) {
        throw new Error('csrf refresh url is required');
    }
    return url;
}

export function applyCsrfToken(token) {
    document.querySelectorAll('input[name="_token"]').forEach((el) => {
        el.value = token;
    });
    document.querySelectorAll('meta[name="csrf-token"]').forEach((el) => {
        el.setAttribute('content', token);
    });
    document.querySelectorAll('[data-csrf-token]').forEach((el) => {
        el.setAttribute('data-csrf-token', token);
        if (window.jQuery) {
            window.jQuery(el).data('csrf-token', token);
        }
    });
}

function expiryRefreshWaitMs(sessionLifetimeMs) {
    const untilBuffer = sessionLifetimeMs - EXPIRY_REFRESH_BUFFER_MS;
    if (untilBuffer > 0) {
        return untilBuffer;
    }
    return Math.max(0, sessionLifetimeMs);
}

function scheduleExpiryRefresh(sessionLifetimeMs) {
    window.clearTimeout(expiryTimerId);
    expiryTimerId = window.setTimeout(() => {
        refreshCsrfToken({ ignoreCooldown: true }).then((ok) => {
            if (!ok) {
                window.location.reload();
            }
        });
    }, expiryRefreshWaitMs(sessionLifetimeMs));
}

export function refreshCsrfToken({ ignoreCooldown = false } = {}) {
    if (!refreshUrl) {
        return Promise.resolve(false);
    }
    if (refreshInFlight) {
        return refreshInFlight;
    }
    if (!ignoreCooldown && Date.now() - lastSuccessfulRefreshAt < REFRESH_COOLDOWN_MS) {
        return Promise.resolve(true);
    }

    refreshInFlight = fetch(refreshUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
    })
        .then((response) => {
            if (!response.ok) {
                return false;
            }
            return response.json().then((payload) => {
                const token = String(payload && payload.csrfToken ? payload.csrfToken : '').trim();
                if (!token) {
                    return false;
                }
                applyCsrfToken(token);
                csrfInitialized = true;
                lastSuccessfulRefreshAt = Date.now();
                const lifetimeMs = Number(payload.sessionLifetimeMs);
                if (expiryRefreshEnabled && Number.isFinite(lifetimeMs) && lifetimeMs > 0) {
                    scheduleExpiryRefresh(lifetimeMs);
                }
                return true;
            });
        })
        .catch(() => false)
        .finally(() => {
            refreshInFlight = null;
        });

    return refreshInFlight;
}

function shouldRefreshCsrfOnSubmit(form) {
    return Boolean(
        form.querySelector('input[name="_token"]')
        || form.hasAttribute('data-csrf-token')
    );
}

function isUsableSubmitter(form, submitter) {
    if (!submitter || submitter.disabled) {
        return false;
    }
    return submitter.form === form;
}

function nativeRequestSubmit(form, submitter) {
    const requestSubmit = HTMLFormElement.prototype.requestSubmit;
    if (typeof requestSubmit === 'function') {
        if (isUsableSubmitter(form, submitter)) {
            requestSubmit.call(form, submitter);
            return;
        }
        requestSubmit.call(form);
        return;
    }
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}

function resubmitForm(form, submitter) {
    window.setTimeout(() => {
        refreshingForms.delete(form);
        form.dataset.csrfFresh = '1';
        try {
            nativeRequestSubmit(form, submitter);
        } catch {
            nativeRequestSubmit(form, null);
        }
    }, 0);
}

function onFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !shouldRefreshCsrfOnSubmit(form)) {
        return;
    }
    if (form.dataset.csrfFresh === '1') {
        delete form.dataset.csrfFresh;
        return;
    }
    if (csrfInitialized && !refreshInFlight) {
        return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    if (refreshingForms.has(form)) {
        return;
    }

    refreshingForms.add(form);
    const submitter = event.submitter;
    refreshCsrfToken().then((ok) => {
        if (!ok) {
            refreshingForms.delete(form);
            window.location.reload();
            return;
        }
        resubmitForm(form, submitter);
    });
}

function onPageShow(event) {
    if (event.persisted) {
        window.location.reload();
    }
}

function onVisibilityChange() {
    if (document.visibilityState !== 'visible') {
        return;
    }
    refreshCsrfToken();
}

export function initCsrfRefresh() {
    if (globalThis[REFRESH_STARTED]) {
        return;
    }
    globalThis[REFRESH_STARTED] = true;
    installJQueryInterceptor();
    installFetchInterceptor();

    refreshUrl = metaContent('csrf-refresh-url');
    if (!refreshUrl) {
        return;
    }
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
}

export function initCsrfSubmitGuard() {
    if (globalThis[SUBMIT_GUARD_STARTED]) {
        return;
    }
    globalThis[SUBMIT_GUARD_STARTED] = true;
    refreshUrl = requireRefreshUrl();
    expiryRefreshEnabled = true;
    initCsrfRefresh();
    document.addEventListener('submit', onFormSubmit, true);
    refreshCsrfToken();
}

function isSameOrigin(url) {
    try {
        return new URL(String(url), window.location.href).origin === window.location.origin;
    } catch (error) {
        return false;
    }
}

function withFreshTokenParam(body, token) {
    if (body instanceof FormData) {
        if (body.has('_token')) {
            body.set('_token', token);
        }
        return body;
    }
    if (body instanceof URLSearchParams) {
        if (!body.has('_token')) {
            return body;
        }
        const params = new URLSearchParams(body);
        params.set('_token', token);
        return params;
    }
    if (typeof body === 'string' && body.includes('_token=')) {
        const params = new URLSearchParams(body);
        params.set('_token', token);
        return params.toString();
    }
    return body;
}

function installJQueryInterceptor() {
    if (!window.jQuery) {
        return;
    }
    window.jQuery.ajaxPrefilter((options) => {
        if (options.crossDomain || !isSameOrigin(options.url)) {
            return;
        }
        const token = metaContent('csrf-token');
        if (!token) {
            return;
        }
        options.headers = Object.assign({}, options.headers, { 'X-CSRF-TOKEN': token });
        options.data = withFreshTokenParam(options.data, token);
    });
}

function isRequestObject(input) {
    return typeof Request === 'function' && input instanceof Request;
}

function mergeHeaderSources(requestHeaders, initHeaders) {
    if (!initHeaders) {
        return requestHeaders;
    }
    if (typeof Headers === 'function') {
        const merged = new Headers(requestHeaders || undefined);
        const extra = initHeaders instanceof Headers ? initHeaders : new Headers(initHeaders);
        extra.forEach((value, key) => {
            merged.set(key, value);
        });
        return merged;
    }
    return Object.assign({}, requestHeaders, initHeaders);
}

function headersWithToken(headers, token) {
    if (typeof Headers === 'function' && headers instanceof Headers) {
        const next = new Headers(headers);
        next.set('X-CSRF-TOKEN', token);
        return next;
    }
    return Object.assign({}, headers, { 'X-CSRF-TOKEN': token });
}

function initWithFreshCsrf(input, init, token) {
    const nextInit = Object.assign({}, init);
    const requestHeaders = isRequestObject(input) ? input.headers : undefined;
    nextInit.headers = headersWithToken(
        mergeHeaderSources(requestHeaders, init && init.headers),
        token
    );
    if (nextInit.body !== undefined) {
        nextInit.body = withFreshTokenParam(nextInit.body, token);
    }
    return nextInit;
}

function installFetchInterceptor() {
    const originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') {
        return;
    }
    window.fetch = function csrfAwareFetch(input, init) {
        const token = metaContent('csrf-token');
        const url = isRequestObject(input) ? input.url : input;
        if (!token || !isSameOrigin(url)) {
            return originalFetch.call(window, input, init);
        }
        return originalFetch.call(window, input, initWithFreshCsrf(input, init, token));
    };
}
